import { useEffect, useRef } from "react";
import type { Question } from "survey-core";
import { Input } from "@rumbledirect/ui-v2";
import { useSurveyQuestionRerender } from "./useSurveyQuestionRerender";

/**
 * Renderer for SurveyJS `text` questions using admin-v2's `Input`. The
 * surrounding chrome (label, description, errors) is rendered by
 * `SurveyQuestionChrome`; this is only the control.
 *
 * Uncontrolled on purpose, mirroring survey-react-ui's own text renderer:
 * the input owns its DOM value while typing and writes to the model through
 * the question's `onChange`/`onKeyUp` handlers, which respect the survey's
 * `textUpdateMode` (on-blur by default — committing `question.value` per
 * keystroke would re-evaluate `visibleIf` expressions mid-word) and feed
 * `question.inputValue`, which is what keeps the visible text in sync.
 *
 * The ref resyncs the DOM when the model's `inputValue` changes from
 * elsewhere — a programmatic `model.setValue()` on a prefill does not reach
 * an uncontrolled input. This is what survey-react-ui's
 * `SurveyQuestionUncontrolledElement.updateDomElement()` does. Comparison
 * only (never an unconditional write): while the user is typing the model
 * already tracks the DOM, so the focused input is never clobbered.
 */
export function SurveyTextQuestion({
  question,
}: {
  readonly question: Question;
}) {
  useSurveyQuestionRerender(question);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputValue = question.inputValue;
  useEffect(() => {
    const control = inputRef.current;
    if (!control) return;
    const next =
      inputValue === undefined || inputValue === null ? "" : String(inputValue);
    if (control.value !== next) {
      control.value = next;
    }
  }, [inputValue]);

  const maxLength = question.getMaxLength();
  return (
    <Input
      ref={inputRef}
      id={question.inputId}
      type={question.inputType}
      defaultValue={
        inputValue === undefined || inputValue === null
          ? ""
          : String(inputValue)
      }
      placeholder={question.renderedPlaceholder ?? ""}
      autoComplete={question.autocomplete ?? undefined}
      autoCapitalize={
        (question.getPropertyValue("autoCapitalize") as string) ?? "none"
      }
      disabled={question.isDisabledAttr}
      readOnly={question.isReadOnlyAttr}
      maxLength={maxLength > 0 ? maxLength : undefined}
      onChange={(event) => question.onChange(event)}
      onBlur={(event) => question.onBlur(event)}
      onFocus={(event) => question.onFocus(event)}
      onKeyUp={(event) => question.onKeyUp(event)}
      onKeyDown={(event) => question.onKeyDown(event)}
      aria-required={question.a11y_input_ariaRequired}
      aria-invalid={question.a11y_input_ariaInvalid}
      aria-describedby={question.a11y_input_ariaDescribedBy ?? undefined}
    />
  );
}
